/**
 * =============================================================================
 * Hook: useReferralPayoutDetails
 * Versão: 1.0.0
 *
 * Descrição:
 *   Carrega TODOS os detalhes de um payout do módulo Indicação em uma única
 *   chamada, via RPC `get_referral_payout_details`. Espelha o padrão usado em
 *   `useCobrancaDetails` (Financeiro / detalhes de cobrança):
 *
 *   - 100% server-side: nada de SELECT direto na tabela.
 *   - Realtime: invalida o cache automaticamente em qualquer UPDATE de
 *     `affiliate_payouts` filtrado pelo `id` do payout aberto.
 *   - Habilita-se apenas quando há um `payoutId` E o Sheet está aberto.
 *
 * Release notes:
 *   v1.0.0 - Criação para alimentar o `PayoutDetailSheet`.
 * =============================================================================
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const logger = createLogger('useReferralPayoutDetails');

export type PayoutStatus = Database['public']['Enums']['affiliate_payout_status'];
export type PayoutMethod = Database['public']['Enums']['affiliate_payout_method'];

/** Estrutura do payout retornada pela RPC. */
export interface PayoutDetailsPayout {
  id: string;
  tenant_id: string;
  affiliate_id: string;
  amount: number;
  currency: string;
  payout_method: PayoutMethod;
  payment_details: Record<string, unknown> | null;
  status: PayoutStatus;
  requested_at: string;
  processed_at: string | null;
  processed_by: string | null;
  paid_at: string | null;
  failed_reason: string | null;
  proof_storage_path: string | null;
  external_transaction_id: string | null;
  commissions_count: number;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/** Dados resumidos do afiliado vinculado. */
export interface PayoutDetailsAffiliate {
  id: string;
  full_name: string | null;
  email: string | null;
  pix_key: string | null;
  pix_key_type: string | null;
}

/** Comissão vinculada ao payout, com origem (deal/cliente/lead/indicação). */
export interface PayoutDetailsCommission {
  id: string;
  reward_value: number | null;
  created_at: string;
  deal_id: string | null;
  deal_title: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_cpf: string | null;
  customer_cnpj: string | null;
  lead_name: string | null;
  referral_id: string | null;
}

/**
 * Evento de auditoria de uma ação executada sobre o payout.
 * Origem: tabela `affiliate_payout_action_logs` (alimentada por trigger).
 *
 * Cada registro responde a "quem executou esta ação, quando e por quê".
 */
export type PayoutActionType =
  | 'created'
  | 'approved'
  | 'paid'
  | 'failed'
  | 'cancelled'
  | 'updated';

export interface PayoutActionLog {
  id: string;
  action: PayoutActionType;
  performed_by: string | null;
  performer_name: string | null;
  performer_email: string | null;
  previous_status: string | null;
  new_status: string | null;
  reason: string | null;
  notes: string | null;
  external_transaction_id: string | null;
  effective_paid_at: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** Estrutura completa retornada pela RPC `get_referral_payout_details`. */
export interface PayoutDetailsResult {
  payout: PayoutDetailsPayout;
  affiliate: PayoutDetailsAffiliate | null;
  commissions: PayoutDetailsCommission[];
  /** Histórico de eventos auditados — quem executou cada ação. */
  action_logs: PayoutActionLog[];
  attachments_count: number;
}

/**
 * Hook principal — busca detalhes do payout + assina realtime.
 *
 * @param payoutId  UUID do payout. `null`/`undefined` desabilita a query.
 * @param open      Se o Sheet está aberto (controla enabled e subscription).
 */
export function useReferralPayoutDetails(
  payoutId: string | undefined | null,
  open: boolean,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // ── Realtime: invalida o cache quando o payout sofre UPDATE ──
  // Usa filtro `id=eq.<payoutId>` para receber só o evento relevante.
  useEffect(() => {
    if (!payoutId || !open) return;

    const channel = supabase
      .channel(`payout-rt-${payoutId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'affiliate_payouts',
          filter: `id=eq.${payoutId}`,
        },
        () => {
          logger.debug('Payout alterado via realtime, invalidando cache', { payoutId });
          queryClient.invalidateQueries({ queryKey: ['referral-payout-details', payoutId] });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliate_commissions',
          filter: `payout_id=eq.${payoutId}`,
        },
        () => {
          logger.debug('Comissão vinculada alterada via realtime', { payoutId });
          queryClient.invalidateQueries({ queryKey: ['referral-payout-details', payoutId] });
        },
      )
      // Auditoria — toda nova ação executada (aprovar/pagar/falhar/cancelar)
      // dispara invalidação para a timeline atualizar com o nome do usuário.
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'affiliate_payout_action_logs',
          filter: `payout_id=eq.${payoutId}`,
        },
        () => {
          logger.debug('Novo log de ação para o payout', { payoutId });
          queryClient.invalidateQueries({ queryKey: ['referral-payout-details', payoutId] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [payoutId, open, queryClient]);

  return useQuery({
    queryKey: ['referral-payout-details', payoutId],
    enabled: !!payoutId && open,
    queryFn: async (): Promise<PayoutDetailsResult> => {
      if (!payoutId) throw new Error('payoutId é obrigatório');

      // Casting até o `types.ts` ser regenerado com a nova RPC.
      const { data, error } = await (supabase.rpc as any)(
        'get_referral_payout_details',
        {
          p_payout_id: payoutId,
          p_tenant_hint: currentTenant?.id ?? null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar detalhes do payout via RPC', error);
        throw error;
      }

      return data as PayoutDetailsResult;
    },
    staleTime: 30 * 1000,
  });
}
